// src/components/FooterSociete.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import axiosInstance from "../axiosInstance";
import { SocieteContext } from "../context/SocieteContext";

import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

/* ------------------------------ Quill ------------------------------ */
const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
Quill.register(SizeStyle, true);
const SIZE_OPTIONS = ["12px", "14px", "16px", "18px", "24px", "32px"];

/* ------------------------------ Aperçu ----------------------------- */
const PREVIEW_SCALE = 0.93;
const PREVIEW_MAX_HEIGHT = 260; // hauteur réduite (visualisation)
const A4_WIDTH_PX = 794;

/* --------------------------- Helpers footer ------------------------ */
const buildFooterFromFields = (i = {}) => {
  const parts = [
    i.nom,
    i.forme,
    i.adresse,
    [i.ville, i.pays].filter(Boolean).join(" – "),
    i.telephone,
    i.email,
    i.site,
    i.tva ? `TVA : ${i.tva}` : "",
    i.ape ? `APE : ${i.ape}` : "",
    i.capital ? `Capital : ${i.capital}` : "",
    i.iban ? `IBAN : ${i.iban}` : "",
    i.immat ? `RCS : ${i.immat}` : "",
    i.assureur ? `Assureur : ${i.assureur}` : "",
  ]
    .filter(Boolean)
    .join(" — ");

  return `<p style="margin:0">${parts || "Votre pied de page ici…"}</p>`;
};

const normalizeInfos = (i = {}) => ({
  nom: i.nom || "",
  forme: i.forme || "",
  adresse: i.adresse || "",
  ville: i.ville || "",
  pays: i.pays || "",
  telephone: i.telephone || "",
  email: i.email || "",
  site: i.site || "",
  tva: i.tva || "",
  ape: i.ape || "",
  capital: i.capital || "",
  iban: i.iban || "",
  immat: i.immat || "",
  assureur: i.assureur || "",
});

/* ===================================================================
   Composant
=================================================================== */
export default function FooterSociete({
  // intégration pop-up (remontée dirty + handler de sauvegarde)
  onDirtyChange = () => { },
  setSaveHandler = () => { },
  // fallback si pas de contexte (facultatif)
  infosSociete: infosProp,
  setInfosSociete: setInfosProp,
}) {
  // Contexte si dispo, sinon props
  const ctx = useContext(SocieteContext) || {};
  const infosSociete = ctx.infosSociete ?? infosProp ?? {};
  const setInfosSociete = ctx.setInfosSociete ?? setInfosProp ?? (() => { });

  const [saving, setSaving] = useState(false);

  const quillModules = useMemo(
    () => ({ toolbar: { container: "#toolbar-footer" } }),
    []
  );
  const quillFormats = [
    "size",
    "bold",
    "italic",
    "underline",
    "align",
    "list",
    "bullet",
    "link",
    "clean",
  ];

  /* --------------------------- Snapshot / Dirty --------------------------- */
  const initialSnapRef = useRef(null);

  const footerHtml = (infosSociete?.footer_html ||
    buildFooterFromFields(infosSociete)
  ).trim();

  const makeSnap = useCallback(() => {
    const base = normalizeInfos(infosSociete);
    return JSON.stringify({
      info: base,
      footer_html: String(footerHtml),
    });
  }, [infosSociete, footerHtml]);

  const isDirty = useMemo(() => {
    if (!initialSnapRef.current) return false;
    return makeSnap() !== initialSnapRef.current;
  }, [makeSnap]);

  useEffect(() => {
    onDirtyChange(!!isDirty);
  }, [isDirty, onDirtyChange]);

  /* -------------------------- Chargement init -------------------------- */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/entreprise");
        // Hydrate le contexte/props
        setInfosSociete((prev) => {
          const merged = { ...prev, ...(data || {}) };
          if (!merged.footer_html) {
            merged.footer_html = buildFooterFromFields(merged); // auto-préremplissage
          }
          return merged;
        });

        // baseline après hydratation
        const mergedNow = { ...(infosSociete || {}), ...(data || {}) };
        const snap = JSON.stringify({
          info: normalizeInfos(mergedNow),
          footer_html: String(
            (data?.footer_html || "").trim() ||
            buildFooterFromFields(mergedNow)
          ),
        });
        initialSnapRef.current = snap;
        onDirtyChange(false);
      } catch (e) {
        console.error("FooterSociete get /entreprise", e);
      }
    })();
  }, [setInfosSociete]);

  /* ------------------------------ Handlers ---------------------------- */
  const onChangeField = (e) => {
    const { name, value } = e.target;
    setInfosSociete((p) => ({ ...p, [name]: value }));
  };
  const onChangeFooterHtml = (html) =>
    setInfosSociete((p) => ({ ...p, footer_html: html }));

  /* ------------------------------- Save ------------------------------- */
  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const payload = {
        tva: (infosSociete?.tva || "").trim(),
        ape: (infosSociete?.ape || "").trim(),
        capital: (infosSociete?.capital || "").trim(),
        iban: (infosSociete?.iban || "").trim(),
        immat: (infosSociete?.immat || "").trim(),
        assureur: (infosSociete?.assureur || "").trim(),
        footer_html: (infosSociete?.footer_html || "").trim(),
      };
      await axiosInstance.put("/entreprise", payload);

      // baseline réinitialisée -> plus de popup tant qu’aucun changement
      initialSnapRef.current = makeSnap();
      onDirtyChange(false);

      alert("✅ Pied de page enregistré.");
    } catch (e) {
      console.error("FooterSociete PUT /entreprise", e);
      alert(
        e?.response?.data?.message ||
        e.message ||
        "Erreur lors de l’enregistrement."
      );
    } finally {
      setSaving(false);
    }
  }, [infosSociete, makeSnap, onDirtyChange]);

  // Exposer au parent pour "Enregistrer et quitter"
  useEffect(() => {
    setSaveHandler(() => handleSave);
    return () => setSaveHandler(null);
  }, [setSaveHandler, handleSave]);

  const fields = [
    {
      name: "tva",
      label: "Numéro d’identification TVA",
      placeholder: "FR XX XXX XXX XXX",
    },
    { name: "ape", label: "Code APE", placeholder: "A322A" },
    { name: "capital", label: "Capital social", placeholder: "Ex : 10 000 €" },
    {
      name: "iban",
      label: "IBAN (et BIC si besoin)",
      placeholder: "FR76…  / BIC : XXXXFRPP",
    },
    {
      name: "immat",
      label: "Immatriculation (SIRET / RCS)",
      placeholder: "Ex : 123 456 789 RCS Paris",
    },
    {
      name: "assureur",
      label: "Garantie & assureur",
      placeholder: "Nom et contrat de l’assureur",
    },
  ];

  /* ------------------------------ UI ------------------------------ */
  return (
    <div className="w-full">
      <style>{`
      .quill14 .ql-editor{font-size:14px}
      .ql-container{max-height:none;overflow:visible}
      .card{border-radius:12px;border:1px solid #e5e7eb;background:#fff}
      .card-hd{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid #eef1f4}
      .card-tt{font-weight:700;color:#0f172a}
      .card-bd{padding:16px}
      .a4-wrap{ --scale:${PREVIEW_SCALE}; width: calc(${A4_WIDTH_PX}px * var(--scale));
                height:${PREVIEW_MAX_HEIGHT}px; overflow:hidden; border:1px solid #e5e7eb; border-radius:12px; background:#fff; box-shadow:0 2px 8px rgba(0,0,0,.03) }
      .a4-inner{ width:${A4_WIDTH_PX}px; height:300px; transform: scale(var(--scale)); transform-origin: top left; position:relative; }

      /* espace sous la DERNIÈRE ligne du WYSIWYG */
      .quill14 .ql-editor::after{
        content:"";
        display:block;
        height:16px; /* marge visuelle en bas */
      }

      /* "ligne imaginaire" (spacer) dans l’aperçu */
      .preview-spacer{
        height:16px;
        border-top:1px dashed transparent; /* mets #e5e7eb si tu veux la voir */
      }
    `}</style>

      {/* CONTAINER CENTRÉ */}
      <div className="mx-auto w-full max-w-[1200px] px-3 sm:px-4 lg:px-6 space-y-6">

        {/* Ligne : Éditeur + Infos légales */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* ÉDITEUR PIED DE PAGE */}
          <section className="card">
            <div className="card-hd">
              <div className="card-tt">Pied de page</div>
            </div>

            <div className="card-bd">
              <div id="toolbar-footer" className="ql-toolbar ql-snow rounded-t-md">
                <select className="ql-size" defaultValue="">
                  <option value="">Défaut</option>
                  {SIZE_OPTIONS.map((v) => (
                    <option key={v} value={v}>{parseInt(v, 10)}</option>
                  ))}
                </select>
                <button className="ql-bold" />
                <button className="ql-italic" />
                <button className="ql-underline" />
                <select className="ql-align" />
                <button className="ql-list" value="ordered" />
                <button className="ql-list" value="bullet" />
                <button className="ql-link" />
                <button className="ql-clean" />
              </div>

              <ReactQuill
                theme="snow"
                value={footerHtml}
                onChange={onChangeFooterHtml}
                placeholder="Compose ton pied de page (tailles en px, gras, liens, etc.)."
                modules={quillModules}
                formats={quillFormats}
                className="quill14 rounded-b-md bg-white"
              />

              <p className="text-[12px] text-slate-500 mt-3">
                Ce texte sera affiché en bas de vos devis et factures.
              </p>
            </div>
          </section>

          {/* INFOS LÉGALES */}
          <section className="card">
            <div className="card-hd">
              <div className="card-tt">Informations légales</div>
            </div>

            <div className="card-bd">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map((f) => (
                  <div key={f.name}>
                    <label className="block text-sm font-medium text-slate-800 mb-1">
                      {f.label}
                    </label>
                    <input
                      type="text"
                      name={f.name}
                      value={infosSociete?.[f.name] || ""}
                      onChange={onChangeField}
                      className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                      placeholder={f.placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>

        {/* BARRE D’ACTIONS COMMUNE (centrée) */}
        <div className="mt-2 flex items-center justify-center">
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-9 px-4 rounded-full text-[13px] bg-sky-700 text-white hover:bg-sky-600 disabled:opacity-60"
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>

        {/* Aperçu centré (compact) */}
        <div className="w-full flex justify-center">
          <div className="a4-wrap">
            <div className="a4-inner">
              <div className="w-full h-full flex items-end">
                <div className="w-full border-t px-6 py-4">
                  <div
                    className="ql-editor !p-0 !leading-snug"
                    dangerouslySetInnerHTML={{ __html: footerHtml }}
                  />
                  <div className="preview-spacer" aria-hidden />
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>{/* /container */}
    </div>
  );
}